import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField
} from '@mui/material'
import { AlertBanner, FormLabel, MButton } from 'components'
import { ADMIN_CONTACT_EMAIL } from 'config'
import type React from 'react'
import { useState } from 'react'
import { isAuthorized, useProvideAuth } from 'utils'

interface EditAssetDialogProps {
  fileName: string
  url: string
  isOpen: boolean
  handleClose: () => void
  setDialogData: any
}

export const EditAssetDialog: React.FC<EditAssetDialogProps> = (
  props: EditAssetDialogProps
) => {
  const [newFileName, setNewFileName] = useState<string>(props.fileName)
  const { renameAssets, handleLogOut } = useProvideAuth()
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false)

  const handleRename = async (): Promise<void> => {
    try {
      await renameAssets(props.fileName, newFileName)

      props.setDialogData({
        type: 'success',
        isOpen: true,
        title: 'Asset has been saved',
        message:
          'You have successfully edited an existing assets. You can find this asset under Assets'
      })

      props.handleClose()
    } catch (error: any) {
      if (!isAuthorized(error)) await handleLogOut()

      if (error.response.data.error === 'Duplicate Asset Name') {
        setIsDuplicate(true)
        return
      }

      props.setDialogData({
        type: 'error',
        isOpen: true,
        title: 'Asset wasn’t saved',
        message: `We are really sorry but there was a problem and we couldn’t save the changes. Please try again, if the error persist, <a href="mailto:${ADMIN_CONTACT_EMAIL}" >Contact Us</a>. `
      })
    }
  }

  return (
    <Dialog open={props.isOpen} onClose={props.handleClose} maxWidth="sm">
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <DialogTitle>Edit Asset</DialogTitle>
        <IconButton onClick={props.handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box
            component="img"
            display="flex"
            alignItems="center"
            src={props.url}
            alt="background"
          />

          <AlertBanner
            type="error"
            message="That name has been used in an existing asset."
            isVisible={isDuplicate}
          />

          <Box>
            <FormLabel>Enter Name</FormLabel>

            <TextField
              fullWidth
              type="text"
              placeholder="Enter Name"
              onChange={(e) => {
                setNewFileName(e.target.value)
                setIsDuplicate(false)
              }}
              value={newFileName}
              error={isDuplicate}
              helperText={isDuplicate ? 'Asset names must be unique' : ''}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            gap={1}
            mt={3}
          >
            <MButton
              variant="contained"
              type="button"
              rounded="true"
              texttransform="none"
              onClick={handleRename}
              disabled={newFileName === props.fileName}
            >
              Save Changes
            </MButton>
            <MButton
              variant="outlined"
              type="button"
              rounded="true"
              texttransform="none"
              onClick={props.handleClose}
              disabled={newFileName === props.fileName}
            >
              Discard Changes
            </MButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
